import type { InfiniteData } from '@tanstack/react-query';

import { Account } from '@/types';
import { InfinityData } from '@/utils/api';

/**
 * Optimistic cache maintenance for the `['MY','account', …]` infinite query
 * (ENG-4586). The list on Settings → Notifications (and every other consumer of
 * the account cache) renders from this cache; the create/edit/delete mutations
 * in `useModifyAccount` only *invalidated* it, so the single refetch raced
 * DynamoDB read-after-write and the change didn't show until a manual refresh.
 *
 * These pure helpers seed the cache with the server-returned `Account` (upsert)
 * or drop it (remove) so the change reflects immediately, mirroring the intent
 * of the pre-existing `useOptimistic` disconnect. The existing invalidation is
 * kept as eventual reconciliation.
 *
 * Accounts are uniquely identified by `Account.key`. Each page's `data` is the
 * page's `Account[]` (see `useMy.ts`). Both helpers preserve every other page
 * field (`offset`/`cursor`/`count`); `removeAccountFromCache` returns the
 * previous reference unchanged when it removes nothing, so an unrelated
 * mutation can't trigger a needless re-render of the list.
 */

type AccountCache = InfiniteData<InfinityData<Account[]>>;

/**
 * Overlay `patch`'s fields onto `base`, but only where `patch` carries a
 * meaningful value — a defined, non-`null`, non-empty-string value. Fields that
 * `patch` omits, or returns as `""`/`null`/`undefined`, keep `base`'s value.
 *
 * This is what makes seeding safe against the backend's partial/blank `update`
 * response (see the call site): the response's empty `username`/`name`/`member`/
 * `updated` are skipped rather than clobbering the cached row, while the fields
 * that actually changed (e.g. `settings`) are applied. Empty-string is never a
 * legitimate value for the identity fields of an account link, so skipping it
 * loses nothing.
 */
function overlayDefinedFields(base: Account, patch: Account): Account {
  const merged: Record<string, unknown> = { ...base };
  Object.entries(patch).forEach(([key, value]) => {
    if (value !== undefined && value !== null && value !== '') {
      merged[key] = value;
    }
  });
  return merged as unknown as Account;
}

/**
 * Insert or merge `account` into the cache, matched by `key`.
 * - If an account with the same `key` exists on any page, the incoming fields
 *   are OVERLAID onto it (shallow merge) — a partial mutation response
 *   (e.g. `update`, which echoes only `value`/`key`/`settings`) preserves the
 *   cached `member`/`username`/`config`; a full response replaces every field.
 * - Otherwise the account is prepended to the first page (a fresh create).
 *
 * No-ops (returns `prev` unchanged) when `account` has no `key` or the cache
 * has no pages yet.
 */
export function upsertAccountInCache(
  prev: AccountCache | undefined,
  account: Account
): AccountCache | undefined {
  if (!prev?.pages?.length || !account?.key) {
    return prev;
  }

  const existingPageIndex = prev.pages.findIndex(page =>
    page.data.some(item => item.key === account.key)
  );

  if (existingPageIndex !== -1) {
    const page = prev.pages[existingPageIndex];
    const current = page.data.find(item => item.key === account.key)!;
    // Overlay only the DEFINED, non-empty fields of the response onto the
    // existing cached row — never replace wholesale, and never clobber with a
    // blank. Mutation responses are unreliable: the backend `update` (PUT)
    // handler echoes the request body unmarshalled into `model.Account`, and
    // `username`/`name`/`member`/`updated` are NOT `omitempty`, so a
    // settings-only PUT returns them as **empty strings** (present, not
    // absent). A plain `{...current, ...account}` shallow merge would therefore
    // overwrite the cached `member` with `""`, and the notifications list
    // (which filters on `member`) would drop the row until a refetch — worse
    // than the original bug. Overlaying only non-empty fields keeps those
    // cached values while still applying what actually changed (e.g. the new
    // `settings` on a severity edit). For a full response (e.g. `link`), every
    // field is non-empty, so the overlay replaces the row wholesale as before.
    // (ENG-4586, codex P1+P2.)
    const merged = overlayDefinedFields(current, account);
    const pages = prev.pages.slice();
    pages[existingPageIndex] = {
      ...page,
      data: page.data.map(item => (item.key === account.key ? merged : item)),
    };
    return { ...prev, pages };
  }

  const [firstPage, ...restPages] = prev.pages;
  return {
    ...prev,
    pages: [{ ...firstPage, data: [account, ...firstPage.data] }, ...restPages],
  };
}

/**
 * Remove the account with `key` from every page of the cache.
 * No-ops (returns `prev` unchanged) when `key` is falsy, the cache has no pages,
 * or no page contained the key.
 */
export function removeAccountFromCache(
  prev: AccountCache | undefined,
  key: string | undefined
): AccountCache | undefined {
  if (!prev?.pages?.length || !key) {
    return prev;
  }

  let removed = false;
  const pages = prev.pages.map(page => {
    if (!page.data.some(item => item.key === key)) {
      return page;
    }
    removed = true;
    return { ...page, data: page.data.filter(item => item.key !== key) };
  });

  return removed ? { ...prev, pages } : prev;
}
